/**
 * Billing Service
 *
 * Data access layer for Stripe-backed subscriptions: read status, start a
 * Checkout Session, and open the Billing Portal.
 */

import { apiGet, apiPost } from '@/lib/api';

const CONTEXT = 'billing';

export type PlanInterval = 'month' | 'year';

/** Mirrors server/src/config/plans.ts — keep the two in step. */
export type PlanTier = 'trial' | 'yoluno' | 'family' | 'family_plus';

/** Paid tiers in upgrade order. `trial` is not purchasable. */
export const PAID_TIERS: PlanTier[] = ['yoluno', 'family', 'family_plus'];

export interface PlanLimits {
  childProfiles: number;
  /** null = no metered cap. */
  storiesPerMonth: number | null;
  lovedOneVoices: number;
  insights: 'basic' | 'enhanced' | 'advanced';
  personalisation: 'trial' | 'standard' | 'enhanced' | 'advanced';
  priorityStories: boolean;
  earlyAccess: boolean;
}

export interface PlanDefinition {
  tier: PlanTier;
  name: string;
  tagline: string;
  limits: PlanLimits;
  /** USD cents. Display only — Stripe is authoritative for charges. */
  price: { month: number; year: number } | null;
}

export interface PlanCatalogue {
  plans: PlanDefinition[];
  trial: PlanDefinition;
}

/** What this account may do right now, and how much of it is used. */
export interface Entitlements {
  tier: PlanTier;
  purchased_tier: PlanTier | null;
  subscription_status: SubscriptionStatus | 'billing_disabled';
  on_free_pass: boolean;
  on_intro_trial: boolean;
  period_start: string;
  limits: PlanLimits;
  usage: {
    stories_used: number;
    stories_limit: number | null;
    child_profiles_used: number;
    child_profiles_limit: number;
    voices_used: number;
    voices_limit: number;
  };
}

// Plan-limit parsing lives in a dependency-free module so it can be unit
// tested without the axios client; re-exported here as the public entry point.
export { limitFromError, type LimitCode, type PlanLimitError } from './planLimits';

/** Format cents as a bare dollar amount, dropping a trailing .00. */
export function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** Effective monthly cost of an annual plan, for the "$7.42/mo billed yearly" line. */
export function monthlyEquivalent(yearCents: number): string {
  return `$${(yearCents / 12 / 100).toFixed(2)}`;
}

// Mirrors Stripe's subscription.status (plus 'none' before any subscription).
export type SubscriptionStatus =
  | 'none'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

export interface BillingStatus {
  billing_enabled: boolean;
  status: SubscriptionStatus;
  plan_interval: PlanInterval | null;
  plan_tier: PlanTier | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  /** Admin-granted free pass expiry (ISO), or null when none. */
  free_pass_until: string | null;
}

/** Statuses that grant access to the app (must match the backend gate). */
export const ACTIVE_STATUSES: SubscriptionStatus[] = ['trialing', 'active'];

export function hasAccess(status: SubscriptionStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

/** True while an admin-granted free pass is still in effect. */
export function freePassActive(until: string | null | undefined): boolean {
  return !!until && new Date(until).getTime() > Date.now();
}

/** Get the current user's subscription status. */
export async function getBillingStatus(): Promise<BillingStatus> {
  return apiGet<BillingStatus>('/billing/status', `${CONTEXT}.getBillingStatus`);
}

/**
 * Create a Checkout Session and return its hosted URL. Pass `promoCode` to
 * redeem a school / free-pass code — a valid code grants a 3-month, no-card
 * free trial that auto-cancels at the end.
 */
export async function createCheckout(
  tier: PlanTier,
  interval: PlanInterval,
  promoCode?: string
): Promise<string> {
  const trimmed = promoCode?.trim();
  const { url } = await apiPost<{ url: string }>(
    '/billing/checkout',
    `${CONTEXT}.createCheckout`,
    { tier, interval, ...(trimmed ? { promoCode: trimmed } : {}) }
  );
  return url;
}

/** The tier catalogue, served from the same config the backend enforces. */
export async function getPlans(): Promise<PlanCatalogue> {
  return apiGet<PlanCatalogue>('/billing/plans', `${CONTEXT}.getPlans`);
}

/** This account's current entitlements and usage. */
export async function getEntitlements(): Promise<Entitlements> {
  return apiGet<Entitlements>('/billing/entitlements', `${CONTEXT}.getEntitlements`);
}

/** Create a Billing Portal session and return its hosted URL. */
export async function createPortalSession(): Promise<string> {
  const { url } = await apiPost<{ url: string }>(
    '/billing/portal',
    `${CONTEXT}.createPortalSession`,
    {}
  );
  return url;
}

export const billingService = {
  getBillingStatus,
  getPlans,
  getEntitlements,
  createCheckout,
  createPortalSession,
  hasAccess,
};
